// Phase C.1 — display helpers for a Division row (migration 036). Grade
// numbers are stored as plain integers (0 = Kindergarten, matching 029's
// own K-8 classification expansion at the Team level) — this file is the
// one place that turns 0/2/3/etc back into "K"/"2nd"/"3rd" for display.
const ORDINAL_GRADE_LABELS = {
  0: 'K', 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th',
  6: '6th', 7: '7th', 8: '8th', 9: '9th', 10: '10th', 11: '11th', 12: '12th',
};

function gradeLabel(grade) {
  return ORDINAL_GRADE_LABELS[grade] ?? String(grade);
}

/** "2nd–3rd", "6th", or null if this Division has no grade_min set. Its
 * own function since both divisionGradeLabel() and divisionSecondaryLabel()
 * (C.3.1) need the grade portion alone, without classification mixed in. */
function gradeRangeLabel(division) {
  if (!division || division.grade_min === null || division.grade_min === undefined) return null;
  return division.grade_min === division.grade_max
    ? gradeLabel(division.grade_min)
    : `${gradeLabel(division.grade_min)}–${gradeLabel(division.grade_max)}`;
}

/** "2nd–3rd", "6th", "JV", or the raw classification string — whichever
 * combination of grade_min/grade_max/classification this Division has
 * set. Falls back to the Division's own name if neither is set (a
 * Division created with only a name, e.g. a fully custom label). */
export function divisionGradeLabel(division) {
  if (!division) return '';
  const parts = [];
  const grade = gradeRangeLabel(division);
  if (grade) parts.push(grade);
  if (division.classification) {
    parts.push(division.classification);
  }
  return parts.length > 0 ? parts.join(' · ') : division.name;
}

/** Phase C.3.1 — a Division-card subtitle that's worth showing, or null
 * when there is nothing beyond what the Division's own name already
 * says. migration 040's backfill names a grade-based Division "6th
 * Grade" / "K–1st Grade" (040 section 3) — divisionGradeLabel() for that
 * SAME row returns "6th" / "K–1st", which is not new information, just a
 * shorter restatement of the name; showing both on a compact card is the
 * literal redundancy this phase's browser testing found ("6th Grade" /
 * "6th"). Real, non-redundant classification (a custom label the grade
 * text doesn't already convey, e.g. "JV" on a Division actually named
 * "Junior Varsity Squad") still surfaces normally. Comparison is
 * case-insensitive substring containment — deliberately simple, matching
 * this file's existing "no fabrication, just formatting" philosophy. */
export function divisionSecondaryLabel(division) {
  if (!division) return null;
  const name = String(division.name || '').toLowerCase();
  const grade = gradeRangeLabel(division);
  const parts = [];
  if (grade && !name.includes(grade.toLowerCase())) parts.push(grade);
  if (division.classification && !name.includes(String(division.classification).toLowerCase())) {
    parts.push(division.classification);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export const COMPETITION_TYPE_LABELS = {
  in_house: 'In-House',
  travel: 'Travel',
  other: 'Other',
};

export function competitionTypeLabel(type) {
  return COMPETITION_TYPE_LABELS[type] || 'Other';
}

// Same palette language as seasonStatus.js — green for the "operating
// normally" states, muted blue-gray for neutral/administrative ones.
export const COMPETITION_TYPE_CHIP_STYLE = {
  in_house: { bgcolor: '#0a2a14', color: '#2ea84b' },
  travel:   { bgcolor: '#0f2136', color: '#6a8aa8' },
  other:    { bgcolor: '#1a2e44', color: '#9eb8d0' },
};

export function competitionTypeChipStyle(type) {
  return COMPETITION_TYPE_CHIP_STYLE[type] || COMPETITION_TYPE_CHIP_STYLE.other;
}

// ═══════════════════════════════════════════════════════════════════════
// Phase C.3.1 — canonical Division ordering
// ═══════════════════════════════════════════════════════════════════════
// ROOT CAUSE of the browser-found "6th Grade, 5th Grade, K–1st Grade"
// ordering bug: migration 040's backfill (which cannot be edited — see
// its own header) assigns each newly-created Division's display_order as
// "one past whatever already exists," in the order its driving cursor
// happens to encounter Teams — `order by team_name asc` (040 section 3).
// For Fort Youth Tackle Football's real data, that's alphabetical Team
// name order (Blackhawks, Disposables, RUNTS, Squirts), which has no
// relationship to grade progression at all. display_order for a
// backfilled Division is therefore an accident of which Team's name
// sorts first, never a deliberate administrator choice — the exact
// opposite of what display_order is meant to represent.
//
// FIX: a reusable, presentation-layer comparator, applied once here and
// consumed by every service function that lists Divisions (see
// listPublicDivisions()/listSeasonDivisions()'s own comments) — no
// migration, no display_order data correction. grade_min/grade_max are
// already present in every Division row this schema returns; that
// structured data is sufficient on its own.
//
// Rule, applied as a strict priority order:
//   1. A Division with a resolved grade_min (0 = Kindergarten counts —
//      never treated as "no grade") always sorts before one without,
//      regardless of either one's display_order — grade is a strictly
//      more meaningful signal than backfill-artifact insertion order.
//   2. Among graded Divisions, grade_min ascending (K -> 1st -> ... ->
//      12th) — the actual youngest-to-oldest progression this phase asks
//      for.
//   3. Within the SAME grade_min (e.g. two Divisions an admin split at
//      the same grade level, or one merged K-1st vs a hypothetical
//      separate K-only), display_order ascending IS honored — this is
//      the one case where two Divisions have no other way to express a
//      deliberate administrator preference, so an explicit reorder via
//      update_division() still works exactly as before.
//   4. Non-grade Divisions (U-based/age-based/named/custom classification
//      — grade_min null) — sorted among themselves by display_order
//      ascending, so an administrator's own arrangement of these is
//      preserved (there is no grade signal to override it with).
//   5. Final deterministic tie-break, either group: name, ascending —
//      never arbitrary row order.
// This never breaks U-based/age-based/named Divisions: they simply have
// no grade_min, so they fall through to rule 4 exactly as they always
// implicitly relied on display_order/row-order before.
function hasResolvedGrade(division) {
  return division && division.grade_min !== null && division.grade_min !== undefined;
}

/** The one comparator every Division list in this app should sort with —
 * pass to Array.prototype.sort directly, or use sortDivisions() below. */
export function compareDivisions(a, b) {
  const aGraded = hasResolvedGrade(a);
  const bGraded = hasResolvedGrade(b);
  if (aGraded !== bGraded) return aGraded ? -1 : 1;
  if (aGraded && bGraded && a.grade_min !== b.grade_min) return a.grade_min - b.grade_min;
  const aOrder = a.display_order ?? 0;
  const bOrder = b.display_order ?? 0;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

/** Non-mutating — returns a new, canonically-ordered array. */
export function sortDivisions(divisions) {
  return [...(divisions || [])].sort(compareDivisions);
}
